import React, { useCallback, useContext, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { profileNethone } from 'nethone';
import { getDeliveryEstimation } from 'staticStoreConfig';

import useMount from 'ggApp/shared/hooks/useMount';
import { PureTranslator } from 'ggApp/modules/translator/components';
import { CARD_TYPE } from 'ggApp/modules/newCheckout/components/InfoCard';
import { CURRENCIES_ASSOCIATION } from 'ggApp/modules/store/constants';
import { CHECKOUT_STEP_NAMES as STEP_NAMES } from 'ggApp/modules/newCheckout/constants';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { deleteOrder } from 'ggApp/shared/context/Order/cookies';
import { findAddressById } from 'ggApp/shared/context/Order/helpers/findAddressById';
import { updateOrderStepState } from 'ggApp/shared/context/Order/services/tracking';
import { MixItemsContainer } from 'ggApp/shared/components/Cart/MixItemsContainer';
import { formatPriceByLanguage } from 'ggApp/utils/money';
import {
    PackageItem,
    ProductPreorderInfoQuery as ProductPreorderInfoQueryType,
    ProductPreorderInfoQueryVariables,
} from 'ggApp/types/graphql';
import ProductPreorderInfoQuery from 'ggApp/shared/queries/productPreorderInfo.graphql';
import { ActiveStore } from 'ggApp/modules/store';
import { StepData } from 'ggApp/shared/types/StepData';

import { SatisfactionSurvey } from './SatisfactionSurvey';

import {
    Button,
    Card,
    Container,
    Header,
    Info,
    MixWrap,
    ProductCard,
    RentInfo,
    ShippingNote,
    ShippingNoteText,
    SmallHeadline,
    SubHeader,
} from './styles';

type ConfirmationProps = {
    className?: string;
    activeStore: ActiveStore;
    stepData: StepData;
    redirectus: (url: string, external: boolean) => void;
    closeCheckout: () => void;
};

export const Confirmation = ({
    className = '',
    stepData,
    activeStore,
    redirectus,
    closeCheckout,
}: ConfirmationProps) => {
    const [includesPreOrderProduct, setIncludesPreOrderProduct] = useState(false);

    const overviewRef = useRef<HTMLTableSectionElement>(null);
    const { heading, subTitle } = stepData;

    const { cart, send, orderMode, userAddresses, current, orderNumber } = useContext(OrderContext);
    const { storeCode, totalPrice, items, paymentMethod, shippingAddressId } = cart;

    useMount(() => {
        if (current.matches('confirmation')) {
            updateOrderStepState({
                orderMode,
                orderNumber,
                currentStep: STEP_NAMES.ORDER_CONFIRMATION,
            });
        }
        overviewRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        profileNethone();
    });

    const resetCart = useCallback(() => {
        send('RESET_CART');
        deleteOrder(orderMode, activeStore.code);
    }, [activeStore, orderMode, send]);

    const handleSubmit = useCallback(() => {
        resetCart();

        Promise.resolve()
            .then(() => closeCheckout())
            .then(() => redirectus(`/${activeStore.code}/your-tech`, true)); // delay for execution dispatchGAEvent
    }, [activeStore, redirectus, resetCart, closeCheckout]);

    const { from, to } = getDeliveryEstimation(storeCode) || {};
    const shippingAddress = findAddressById({ userAddresses, addressId: shippingAddressId });
    const itemSkus: string[] = [];
    (items as PackageItem[])?.forEach(
        (item) => item?.variant?.product?.sku && itemSkus.push(item?.variant?.product?.sku),
    );

    useQuery<ProductPreorderInfoQueryType, ProductPreorderInfoQueryVariables>(
        ProductPreorderInfoQuery,
        {
            variables: {
                productSku: itemSkus,
            },
            onCompleted: (data) => {
                const preordersFound = data.products?.[0].variants?.some(
                    (v) => v.preorder !== null,
                );
                setIncludesPreOrderProduct(!!preordersFound);
            },
        },
    );

    return (
        <Container
            ref={overviewRef}
            data-testid="confirmation-test-id"
            className={`${className} tiktok-order-confirmation`}
        >
            <Header>
                <PureTranslator tk={heading} />
            </Header>

            <SubHeader>
                <PureTranslator tk={subTitle} />
            </SubHeader>

            <SatisfactionSurvey />

            {Array.isArray(items) &&
                (orderMode === 'MIX' ? (
                    <MixWrap>
                        <MixItemsContainer shouldShowLink={false} items={items} isConfirmation />
                    </MixWrap>
                ) : (
                    items.map((item) => <ProductCard key={item.id} item={item} />)
                ))}
            <Card
                readOnly
                titleTk="NEW_CHECKOUT_SUMMARY_ADDRESS"
                type={CARD_TYPE.address}
                hideShippingDates={includesPreOrderProduct}
                address={{
                    ...shippingAddress,
                    delivery: from && to ? `${from}-${to}` : null,
                }}
            />

            <Card
                readOnly
                titleTk="NEW_CHECKOUT_SUMMARY_PAYMENT"
                type={CARD_TYPE.payment}
                payment={paymentMethod}
            />

            <RentInfo>
                <SmallHeadline>
                    <PureTranslator tk="CHECKOUT_ORDER_TOTAL" />
                </SmallHeadline>
                <SmallHeadline>
                    {formatPriceByLanguage(
                        totalPrice,
                        CURRENCIES_ASSOCIATION[activeStore.default_currency],
                        activeStore.activeLanguage,
                    )}
                </SmallHeadline>
            </RentInfo>

            <ShippingNote>
                <Info color="rgba(51, 51, 51, 0.75)" />
                <ShippingNoteText>
                    <PureTranslator tk="CART_ADDRESS_SHIPPING_NOTE" />
                </ShippingNoteText>
            </ShippingNote>

            <Button fullWidth onClick={handleSubmit} data-testid="confirmation-cta-test-id">
                <SmallHeadline>
                    <PureTranslator tk="NEW_CHECKOUT_CONFIRMATION_CTA" />
                </SmallHeadline>
            </Button>
        </Container>
    );
};
